import { useState, useEffect, useCallback } from 'react';
import {
  getStatistics,
  getProblemHistory,
  addProblemToHistory,
  clearStatistics,
  clearProblemHistory,
} from '../utils/storage';

/**
 * Custom hook for managing problem history and statistics
 */
export function useStatistics() {
  const [statistics, setStatistics] = useState(null);
  const [problemHistory, setProblemHistory] = useState([]);

  // Load initial data
  useEffect(() => {
    setStatistics(getStatistics());
    setProblemHistory(getProblemHistory());
  }, []);

  // Track a problem attempt
  const trackProblem = useCallback((problemData) => {
    const history = addProblemToHistory(problemData);
    setProblemHistory(history);

    // Refresh statistics
    setStatistics(getStatistics());
  }, []);

  // Clear all statistics and history
  const clearAllStats = useCallback(() => {
    clearStatistics();
    clearProblemHistory();
    setStatistics(getStatistics());
    setProblemHistory([]);
  }, []);

  // Calculate accuracy percentage
  const getAccuracyPercentage = useCallback(() => {
    if (!statistics || statistics.totalProblems === 0) return 0;
    return Math.round((statistics.correctAnswers / statistics.totalProblems) * 100);
  }, [statistics]);

  // Get accuracy for specific operation
  const getOperationAccuracy = useCallback((operation) => {
    if (!statistics) return { correct: 0, wrong: 0, percentage: 0 };

    const opStats = statistics.byOperation[operation];
    const total = opStats.correct + opStats.wrong;

    return {
      ...opStats,
      total,
      percentage: total > 0 ? Math.round((opStats.correct / total) * 100) : 0,
    };
  }, [statistics]);

  // Get accuracy for specific difficulty
  const getDifficultyAccuracy = useCallback((difficulty) => {
    if (!statistics) return { correct: 0, wrong: 0, percentage: 0 };

    const diffStats = statistics.byDifficulty[difficulty];
    const total = diffStats.correct + diffStats.wrong;

    return {
      ...diffStats,
      total,
      percentage: total > 0 ? Math.round((diffStats.correct / total) * 100) : 0,
    };
  }, [statistics]);

  // Identify weakest operation (needs most practice)
  const getWeakestOperation = useCallback(() => {
    if (!statistics) return null;

    const operations = ['plus', 'minus', 'gange'];
    let weakest = null;
    let lowestAccuracy = 100;

    operations.forEach((op) => {
      const accuracy = getOperationAccuracy(op);
      if (accuracy.total >= 5 && accuracy.percentage < lowestAccuracy) {
        lowestAccuracy = accuracy.percentage;
        weakest = {
          operation: op,
          ...accuracy,
        };
      }
    });

    return weakest;
  }, [statistics, getOperationAccuracy]);

  // Get suggested difficulty based on recent performance
  const getSuggestedDifficulty = useCallback(() => {
    if (!statistics || statistics.totalProblems < 10) return null;

    const overallAccuracy = getAccuracyPercentage();

    // Suggest easier difficulty if accuracy < 60%
    if (overallAccuracy < 60) {
      return { suggestion: 'let', reason: 'Prøv en nemmere sværhedsgrad for at øve mere' };
    }

    // Suggest harder difficulty if accuracy > 90%
    if (overallAccuracy > 90 && statistics.totalProblems >= 20) {
      return { suggestion: 'svært', reason: 'Du klarer det rigtig godt! Prøv en sværere udfordring' };
    }

    return null;
  }, [statistics, getAccuracyPercentage]);

  return {
    statistics,
    problemHistory,
    trackProblem,
    clearAllStats,
    getAccuracyPercentage,
    getOperationAccuracy,
    getDifficultyAccuracy,
    getWeakestOperation,
    getSuggestedDifficulty,
  };
}
